"use client";

import { c as _c } from "react/compiler-runtime";
import { IconAlertWarningOutline18, IconCircleCheckOutline18, IconDatabaseOutline18, IconEyeOutline18, IconPlugOutline18, IconPlusOutline18, IconSliders2Outline18 } from "nucleo-ui-outline-18";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { GlassIcon } from "@/components/ui/glass-icon";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useActions, useTriggers } from "@/hooks/useActionTriggerDefinitions";
import { useOrgTables, useExecuteQuery } from "@/hooks/useOrgDatabase";
import { type DBTableReference, extractDBQueryTables } from "@/lib/workflow/dbQueryTables";
import { ALLOWED_COLUMN_TYPES, SERIAL_TYPES, escapeIdent, RESERVED_TABLE_PREFIXES, SAFE_DEFAULT_PATTERN } from "@/lib/db/sqlSafety";
import { enrichPropsWithInputType } from "@/lib/workflow/suggestTemplateProps";
import { resolveCsvTableHumanToAtomic, tokenAmountToString } from "@/lib/workflow/tokenAmount";
import { tokenInfoKey, useSimTokensInfo } from "@/hooks/useSimTokenInfo";
import type { ActionSchemaLookup } from "@/lib/templates/resolveChainFilter";
import { useConnectorsStore } from "@/stores/connectorsStore";
import type { TableSchema, TemplateProp } from "@/stores/templatesStore";
import type { WorkflowDefinition } from "@/stores/workflowsStore";
import { useWalletsStore } from "@/stores/walletsStore";
import type { AIWorkflowDefinition } from "@/types/aiChat";
import { cn } from "@/lib/utils";
import { CreateTableModal } from "@/app/(v2)/databases/_components/CreateTableModal";
import type { ColumnDef } from "@/app/(v2)/databases/_components/CreateTableModal";
import Link from "next/link";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import NotificationChannelPicker from "@/components/notification/NotificationChannelPicker";
import { GOOGLE_SHEET_TEMPLATE_URL_PROP_KEY, GOOGLE_SHEETS_NODE_PREFIX, type NotificationConfig } from "@/lib/workflow/notificationChannels";
import { toast } from "@/lib/toast";
import ConnectorSelector from "./ConnectorSelector";
import EventAbiSelector from "./form-fields/EventAbiSelector";
import FilterBuilder from "./form-fields/FilterBuilder";
import { useNoAutofillScope } from "./form-fields/NoAutofillScope";
import TemplatePropValueInput from "./form-fields/TemplatePropValueInput";
import { buildCellContext, DEFAULT_CHAIN_PROP_KEY, isCsvTableValueValid, type TokenInfo } from "./form-fields/csvTableCells";
import type { CsvTableValue } from "./form-fields/CsvTableInput";
import TokenChainAmountInput from "./form-fields/TokenChainAmountInput";
import TokenChainInput from "./form-fields/TokenChainInput";
import WalletSelector from "./WalletSelector";

/** Coerce a template prop default value to match the declared prop type. */
function coercePropDefault(prop: TemplateProp): PropValue {
  const val = prop.default;
  if (val === undefined || val === null) return undefined;
  // Token amounts must be strings (smallest units). The DB may store them as
  // numbers from older templates — stringify them consistently.
  if (prop.properties?.inputType === "tokenAmount") {
    return tokenAmountToString(val);
  }
  if ((prop.type === "number" || prop.type === "integer") && typeof val !== "number") {
    const n = Number(val);
    return isNaN(n) ? undefined : n;
  }
  if (prop.type === "boolean" && typeof val !== "boolean") {
    return val === "true";
  }
  return val as PropValue;
}

/** Heuristic: infer a column type from the column name. Falls back to TEXT. */
const NUMERIC_HINTS = /^(amount|price|cost|total|count|quantity|balance|rate|fee|score|weight|size|num_|number_)($|_)/i;
const INTEGER_HINTS = /^(id|.+_count|age|rank|sort_order|display_order|level|version|status_code)$/i;
const REAL_HINTS = /^(latitude|longitude|lat|lng|lon|percent|percentage|ratio|avg_|average_)$/i;
const JSON_HINTS = /^(metadata|config|settings|extra|data|payload|options|params|attributes|properties|tags|labels|context)$/i;
const BOOLEAN_PREFIX = /^(is_|has_|can_|should_)/i;
const BOOLEAN_EXACT = /^(enabled|active|visible|deleted|archived)$/i;
const VALID_SQL_IDENTIFIER = /^[a-zA-Z][a-zA-Z0-9_]*$/;
const TIMESTAMP_PATTERN = /_(at|date|time|timestamp)$/i;
const TIMESTAMP_PREFIX_PATTERN = /^(created|updated|expires|started|finished)$/i;

/** Design-principle columns added to every auto-created table. */
const AUTO_COLUMN_DEFS: ColumnDef[] = [{
  name: "id",
  type: "SERIAL",
  primaryKey: true,
  notNull: true,
  unique: false,
  defaultValue: ""
}, {
  name: "workflow_id",
  type: "TEXT",
  primaryKey: false,
  notNull: true,
  unique: false,
  defaultValue: ""
}];
const AUTO_COLUMN_DEFS_TAIL: ColumnDef[] = [{
  name: "created_at",
  type: "TIMESTAMPTZ",
  primaryKey: false,
  notNull: false,
  unique: false,
  defaultValue: "NOW()"
}, {
  name: "updated_at",
  type: "TIMESTAMPTZ",
  primaryKey: false,
  notNull: false,
  unique: false,
  defaultValue: "NOW()"
}];
const AUTO_COLUMN_NAMES = new Set([...AUTO_COLUMN_DEFS.map(c => c.name), ...AUTO_COLUMN_DEFS_TAIL.map(c => c.name)]);
function buildCreateTableSQL(tableName: string, schema: TableSchema): string {
  if (schema.columns.length === 0) {
    throw new Error(`Table schema for "${tableName}" has no columns`);
  }
  const pkCols = schema.columns.filter(c => c.primaryKey);
  const compositePK = pkCols.length > 1;
  const colDefs = schema.columns.map(col => {
    const upperType = col.type.toUpperCase();
    // SERIAL/BIGSERIAL/SMALLSERIAL are implicitly NOT NULL with a sequence default —
    // skip explicit NOT NULL and any user-supplied DEFAULT (the sequence is the default).
    if (SERIAL_TYPES.has(upperType)) {
      let def = `${escapeIdent(col.name)} ${upperType}`;
      if (col.primaryKey && !compositePK) {
        def += " PRIMARY KEY";
      }
      if (col.unique) def += " UNIQUE";
      return def;
    }
    const safeType = ALLOWED_COLUMN_TYPES.has(upperType) ? col.type : "TEXT";
    let def = `${escapeIdent(col.name)} ${safeType}`;
    if (col.notNull) def += " NOT NULL";
    if (col.primaryKey && !compositePK) {
      def += " PRIMARY KEY";
    }
    if (col.unique) def += " UNIQUE";
    if (col.default !== undefined) {
      if (SAFE_DEFAULT_PATTERN.test(col.default)) {
        if (/^\w+\([^)]*\)$/i.test(col.default)) {
          def += ` DEFAULT (${col.default})`;
        } else {
          def += ` DEFAULT ${col.default}`;
        }
      }
    }
    return def;
  });
  if (compositePK) {
    colDefs.push(`PRIMARY KEY (${pkCols.map(c => escapeIdent(c.name)).join(", ")})`);
  }
  return `CREATE TABLE ${escapeIdent(tableName)} (\n  ${colDefs.join(",\n  ")}\n)`;
}
function inferColumnType(colName: string): string {
  if (INTEGER_HINTS.test(colName)) return "INTEGER";
  if (REAL_HINTS.test(colName)) return "REAL";
  if (NUMERIC_HINTS.test(colName)) return "REAL";
  if (JSON_HINTS.test(colName)) return "JSON";
  if (TIMESTAMP_PATTERN.test(colName) || TIMESTAMP_PREFIX_PATTERN.test(colName)) return "TIMESTAMPTZ";
  if (BOOLEAN_PREFIX.test(colName) || BOOLEAN_EXACT.test(colName)) return "BOOLEAN";
  return "TEXT";
}
const AUTO_TIMESTAMP_DEFAULT = /^(created|updated)(_at|_date|_time|_timestamp)?$/i;
function inferDefaultValue(colName: string, colType: string): string {
  if (AUTO_TIMESTAMP_DEFAULT.test(colName)) return "NOW()";
  // Use a single-quoted SQL string literal so SAFE_DEFAULT_PATTERN accepts it
  // and Postgres casts it to JSON/JSONB automatically.
  if (colType === "JSON" || colType === "JSONB") return "'{}'";
  return "";
}
const SQL_KEYWORDS = /^(CURRENT_TIMESTAMP|CURRENT_DATE|CURRENT_TIME|NULL|TRUE|FALSE)$/i;
function formatDefaultSQL(defaultValue: string): string {
  if (!defaultValue) return "";
  const v = defaultValue.trim();
  if (/^\w+\([^)]*\)$/i.test(v)) return ` DEFAULT (${v})`;
  if (SQL_KEYWORDS.test(v)) return ` DEFAULT ${v}`;
  if (/^-?\d+(\.\d+)?$/.test(v)) return ` DEFAULT ${v}`;
  return ` DEFAULT '${v.replace(/'/g, "''")}'`;
}

/** Filter valid column hints and infer types + defaults in a single pass. */
function buildInferredColumns(hints: string[]): ColumnDef[] {
  return hints.filter(col => !AUTO_COLUMN_NAMES.has(col.toLowerCase()) && VALID_SQL_IDENTIFIER.test(col)).map(col => {
    const type = inferColumnType(col);
    return {
      name: col,
      type,
      primaryKey: false,
      notNull: false,
      unique: false,
      defaultValue: inferDefaultValue(col, type)
    };
  });
}

/** Connector types that should be rendered as wallet selectors (covers legacy "turnkey" type). */
const WALLET_TYPES = new Set(["wallet", "turnkey"]);
const isWalletType = (type: string) => WALLET_TYPES.has(type);
export interface RequiredConnector {
  /** Unique key to identify this connector requirement (e.g., nodeId) */
  key: string;
  /** The connector type required (e.g., "slack", "discord", "wallet") */
  type: string;
  /** Optional label to show what this connector is for */
  label?: string;
  /** Optional description */
  description?: string;
  /** When true, this connector is optional — user may skip it (used by blocks with conditional channels) */
  optional?: boolean;
  /** Filter wallets by account type (e.g., "polymarket_deposit_wallet") */
  accountType?: string;
}

/** Values for template props — matches the union of TemplateProp.type possibilities */
export type PropValue = string | number | boolean | string[] | CsvTableValue | undefined;
function isTemplateValuePresent(value: PropValue): boolean {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}
function MissingInputFrame(t0) {
  const $ = _c(11);
  const {
    active,
    message,
    children
  } = t0;
  const t1 = active ? "true" : undefined;
  const t2 = active && "border-red-500 bg-red-50/50 ring-2 ring-red-500/10 dark:bg-red-950/20";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("relative -m-3 rounded-xl border border-dashed border-transparent p-3 transition-colors", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== active || $[3] !== message) {
    t4 = active && <span className="text-2xs pointer-events-none absolute right-3 top-0 z-10 -translate-y-1/2 rounded-full bg-red-600 px-2 py-0.5 font-semibold leading-none text-white shadow-sm">{message}</span>;
    $[2] = active;
    $[3] = message;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== active || $[6] !== children || $[7] !== t1 || $[8] !== t3 || $[9] !== t4) {
    t5 = <div data-template-missing={t1} aria-invalid={active} className={t3}>{t4}{children}</div>;
    $[5] = active;
    $[6] = children;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

/**
 * Compute the minimum number of connectors that must be selected.
 * When optional connectors exist, only mandatory ones are required (may be zero).
 * Returns undefined when all connectors are required (i.e., minConnectors === total count).
 */
export function computeMinConnectors(connectors: RequiredConnector[]): number | undefined {
  const hasOptional = connectors.some(r => r.optional);
  if (!hasOptional) return undefined;
  const mandatoryCount = connectors.filter(r => !r.optional).length;
  return mandatoryCount;
}
interface ConnectorInputModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** List of connectors required by the template */
  requiredConnectors: RequiredConnector[];
  /** Called when user submits the form with selected connector IDs and prop values */
  onSubmit: (connectorMapping: Record<string, string>, propValues?: Record<string, PropValue>, payloadOverrides?: Record<string, Record<string, unknown>>) => void;
  /** Whether the submission is in progress */
  isSubmitting?: boolean;
  /** Optional title override */
  title?: string;
  /** Optional description override */
  description?: string;
  /** Template props that the user needs to fill in */
  templateProps?: TemplateProp[];
  /**
   * Minimum number of connectors that must be selected.
   * Defaults to total requiredConnectors count (all required).
   * Set to a lower number for blocks/templates where only some connectors are needed
   * (e.g., 1 = user must select at least one channel).
   */
  minConnectors?: number;
  /** Workflow definition — used to enrich template props with rich input types from action schemas */
  definition?: WorkflowDefinition | AIWorkflowDefinition;
  /** Database tables required by db-query nodes in the template */
  requiredTables?: DBTableReference[];
  tableSchemas?: Record<string, TableSchema>;
}
export default function ConnectorInputModal({
  open,
  onOpenChange,
  requiredConnectors,
  onSubmit,
  isSubmitting = false,
  title = "Configure Connectors",
  description = "This template requires connectors to work. Please select the connectors you want to use.",
  templateProps,
  minConnectors,
  definition,
  requiredTables,
  tableSchemas
}: ConnectorInputModalProps) {
  const connectors = useConnectorsStore(state => state.connectors);
  const loadConnectors = useConnectorsStore(state_0 => state_0.loadConnectors);
  const wallets = useWalletsStore(state_1 => state_1.wallets);
  const loadWallets = useWalletsStore(state_2 => state_2.loadWallets);
  const [isLoadingConnectors, setIsLoadingConnectors] = useState(true);
  const {
    actions
  } = useActions();
  const {
    triggers
  } = useTriggers();
  const defMap = useMemo(() => new Map([...actions, ...triggers].map(def => [def.type, def])), [actions, triggers]);

  // Enrich template props with rich inputType from action schema format hints
  // (e.g., slack-channel, telegram-chat) so older templates get rich selectors
  const enrichedProps = useMemo(() => {
    if (!templateProps?.length) return templateProps;
    const cloned = templateProps.map(p => ({
      ...p,
      properties: p.properties ? {
        ...p.properties
      } : undefined
    }));
    if (definition) {
      enrichPropsWithInputType(cloned, definition, defMap);
    }
    // Fallback for google-sheets URL props that schema enrichment missed
    // (legacy templates without `sources`, or action defs not loaded yet).
    if (definition?.nodes) {
      const nodes = definition.nodes as Record<string, {
        type?: string;
        payload?: Record<string, unknown>;
      }>;
      const gsNodes = Object.values(nodes).filter(n => n.type?.startsWith(GOOGLE_SHEETS_NODE_PREFIX));
      if (gsNodes.length > 0) {
        for (const prop of cloned) {
          if (prop.properties?.inputType) continue;
          const sources = prop.properties?.sources as Array<{
            nodeId: string;
            fieldPath: string;
          }> | undefined;
          if (sources?.length) {
            const matchesSource = sources.some(s => {
              const nodeType = nodes[s.nodeId]?.type;
              return nodeType?.startsWith(GOOGLE_SHEETS_NODE_PREFIX) && s.fieldPath === "url";
            });
            if (matchesSource) {
              if (!prop.properties) prop.properties = {};
              prop.properties!.inputType = "googleSheetUrl";
              continue;
            }
          }
          const propRef = `{{$props.${prop.key}}}`;
          for (const node of gsNodes) {
            if ((node.payload as Record<string, unknown> | undefined)?.url === propRef) {
              if (!prop.properties) prop.properties = {};
              prop.properties!.inputType = "googleSheetUrl";
              break;
            }
          }
        }
      }
    }
    return cloned;
  }, [templateProps, definition, defMap]);
  const nodeTypesByNodeId = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    if (!definition?.nodes) return map;
    for (const [nodeId, node_0] of Object.entries(definition.nodes)) {
      const type = (node_0 as {
        type?: string;
      } | null | undefined)?.type;
      if (type) map[nodeId] = type;
    }
    return map;
  }, [definition]);
  const actionByType = useMemo<ActionSchemaLookup>(() => {
    const map_0: ActionSchemaLookup = {};
    for (const a of actions) {
      if (a.type) {
        map_0[a.type] = {
          payloadSchema: a.payloadSchema as ActionSchemaLookup[string]["payloadSchema"]
        };
      }
    }
    return map_0;
  }, [actions]);
  const notificationProp = useMemo(() => enrichedProps?.find(p_0 => p_0.key === "$notification") ?? null, [enrichedProps]);
  const notificationAllowedChannels = useMemo<string[] | undefined>(() => {
    const raw = notificationProp?.properties?.allowedChannels;
    if (!Array.isArray(raw)) return undefined;
    const filtered = raw.filter((v): v is string => typeof v === "string");
    return filtered.length > 0 ? filtered : undefined;
  }, [notificationProp]);
  const googleSheetTemplateProp = useMemo(() => enrichedProps?.find(p_1 => p_1.key === GOOGLE_SHEET_TEMPLATE_URL_PROP_KEY) ?? null, [enrichedProps]);
  // Detect evm-log trigger that needs ABI selection
  const evmLogConfig = useMemo(() => {
    if (!definition?.nodes) return null;
    const nodes_0 = definition.nodes as Record<string, {
      type?: string;
      payload?: Record<string, unknown>;
    }>;
    const rootNode = nodes_0.root;
    if (!rootNode || rootNode.type !== "evm-log") return null;
    const payload = rootNode.payload;
    if (!payload) return null;
    const eventAbi = payload.eventAbi;
    const hasEventAbi = typeof eventAbi === "string" && eventAbi !== "" || typeof eventAbi === "object" && eventAbi !== null;
    if (hasEventAbi) return null;
    const chainPropKey = enrichedProps?.find(p_2 => p_2.properties?.inputType === "chainSelector" && p_2.properties?.sources?.some((s_0: {
      nodeId?: string;
    }) => s_0.nodeId === "root"))?.key ?? null;
    const contractPropKey = enrichedProps?.find(p_3 => (p_3.properties?.inputType === "contractAddress" || p_3.properties?.inputType === "address") && p_3.properties?.sources?.some((s_1: {
      nodeId?: string;
      fieldPath?: string;
    }) => s_1.nodeId === "root" && s_1.fieldPath === "contractAddress"))?.key ?? null;
    return {
      chainPropKey,
      contractPropKey
    };
  }, [definition, enrichedProps]);
  const [selectedEventAbi, setSelectedEventAbi] = useState<string | undefined>();
  const [selectedFilter, setSelectedFilter] = useState<string | undefined>();

  // Hide chain props that are controlled by a morphoVault selector's built-in chain filter
  const morphoChainPropKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const p_4 of enrichedProps ?? []) {
      if (p_4.properties?.inputType === "morphoVault" && p_4.properties?.chainPropKey) {
        keys.add(p_4.properties.chainPropKey);
      }
    }
    return keys;
  }, [enrichedProps]);
  const regularProps = useMemo(() => enrichedProps?.filter(p_5 => p_5.key !== "$notification" && p_5.key !== GOOGLE_SHEET_TEMPLATE_URL_PROP_KEY && !morphoChainPropKeys.has(p_5.key)), [enrichedProps, morphoChainPropKeys]);

  // Group props by groupKey and determine component type per group
  const {
    propGroups,
    groupComponents
  } = useMemo(() => {
    const groups = new Map<string, NonNullable<typeof regularProps>>();
    const components = new Map<string, "tokenChainAmount" | "tokenChain">();
    if (!regularProps) return {
      propGroups: groups,
      groupComponents: components
    };
    for (const prop_0 of regularProps) {
      const gk = prop_0.properties?.groupKey;
      if (gk) {
        if (!groups.has(gk)) groups.set(gk, []);
        groups.get(gk)!.push(prop_0);
      }
    }
    for (const [gk_0, gProps] of groups) {
      const hasChain = gProps.some(p_6 => p_6.properties?.inputType === "chainSelector");
      const hasToken = gProps.some(p_7 => p_7.properties?.inputType === "tokenSelector");
      const hasAmount = gProps.some(p_8 => p_8.properties?.inputType === "tokenAmount");
      if (hasChain && hasToken && hasAmount) {
        components.set(gk_0, "tokenChainAmount");
      } else if (hasChain && hasToken) {
        components.set(gk_0, "tokenChain");
      }
    }
    return {
      propGroups: groups,
      groupComponents: components
    };
  }, [regularProps]);

  // Form state: maps connector key to selected connector ID
  const [selectedConnectors, setSelectedConnectors] = useState<Record<string, string>>({});
  const [workflowWalletAddress, setWorkflowWalletAddress] = useState<string>();
  const [insufficientGroups, setInsufficientGroups] = useState<Set<string>>(new Set());
  const [sheetValidation, setSheetValidation] = useState<Record<string, boolean>>({});
  const [showMissingHints, setShowMissingHints] = useState(false);
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const autoSelectDoneRef = useRef(false);
  useNoAutofillScope(modalContentRef);
  const [propValues, setPropValues] = useState<Record<string, PropValue>>(() => {
    const initial: Record<string, PropValue> = {};
    (enrichedProps ?? templateProps)?.forEach(prop_1 => {
      if (prop_1.default !== undefined) {
        initial[prop_1.key] = coercePropDefault(prop_1);
      } else if (prop_1.type === "array") {
        initial[prop_1.key] = [];
      } else {
        initial[prop_1.key] = undefined;
      }
    });
    return initial;
  });

  // Collect every (propKey, colKey) → (chainId, tokenAddress) tuple across
  // all csvTable props in the template. Owning this lookup here (instead of
  // inside each CsvTableInput) means we make ONE useSimTokensInfo call per
  // modal — cleaner than every grid duplicating the query — and we can
  // surface decimals both to cells (for the helper line / validator) and to
  // the submit-time human→atomic conversion.
  const csvTableTokenLookups = useMemo(() => {
    const out: Array<{
      propKey: string;
      colKey: string;
      tokenAddress: string;
      chainId: number;
    }> = [];
    for (const prop_2 of enrichedProps ?? []) {
      if (prop_2.properties?.inputType !== "csvTable") continue;
      for (const col of prop_2.properties?.columns ?? []) {
        if (col.inputType !== "tokenAmount") continue;
        const chainRaw = col.chainPropKey ? propValues[col.chainPropKey] : col.chainId ?? propValues[DEFAULT_CHAIN_PROP_KEY];
        const tokenRaw = col.tokenAddressPropKey ? propValues[col.tokenAddressPropKey] : col.tokenAddress;
        const chainId = typeof chainRaw === "number" ? chainRaw : Number(chainRaw);
        const tokenAddress = typeof tokenRaw === "string" ? tokenRaw : "";
        if (!Number.isFinite(chainId) || chainId <= 0 || !tokenAddress) continue;
        out.push({
          propKey: prop_2.key,
          colKey: col.key,
          tokenAddress,
          chainId
        });
      }
    }
    return out;
  }, [enrichedProps, propValues]);
  const {
    tokenInfoByKey,
    isLoading: isTokenInfoLoading
  } = useSimTokensInfo(csvTableTokenLookups.map(({
    tokenAddress: tokenAddress_0,
    chainId: chainId_0
  }) => ({
    tokenAddress: tokenAddress_0,
    chainId: chainId_0
  })), csvTableTokenLookups.length > 0);

  /** `<propKey>:<colKey>` → TokenInfo. Fed into TemplatePropValueInput so it can slice per prop. */
  const csvTableTokenInfo = useMemo(() => {
    const out_0: Record<string, TokenInfo | undefined> = {};
    for (const {
      propKey,
      colKey,
      tokenAddress: tokenAddress_1,
      chainId: chainId_1
    } of csvTableTokenLookups) {
      const info = tokenInfoByKey[tokenInfoKey(tokenAddress_1, chainId_1)];
      if (info) {
        out_0[`${propKey}:${colKey}`] = {
          decimals: info.decimals,
          symbol: info.symbol,
          name: info.name
        };
      }
    }
    return out_0;
  }, [csvTableTokenLookups, tokenInfoByKey]);

  /** True when at least one declared csvTable tokenAmount column is still
   *  waiting on metadata. Blocks Continue so humanToAtomic at submit can't
   *  silently skip columns whose decimals haven't arrived yet. */
  const csvTableTokenInfoPending = useMemo(() => {
    if (isTokenInfoLoading) return true;
    return csvTableTokenLookups.some(({
      propKey: propKey_0,
      colKey: colKey_0
    }) => !csvTableTokenInfo[`${propKey_0}:${colKey_0}`]);
  }, [isTokenInfoLoading, csvTableTokenLookups, csvTableTokenInfo]);
  const contractValue = evmLogConfig?.contractPropKey ? propValues[evmLogConfig.contractPropKey] : undefined;
  useEffect(() => {
    setSelectedEventAbi(undefined);
    setSelectedFilter(undefined);
  }, [contractValue]);
  const resolvedTables = useMemo(() => {
    if (!definition) return requiredTables ?? [];
    return extractDBQueryTables(definition as Parameters<typeof extractDBQueryTables>[0], propValues as Record<string, unknown>);
  }, [definition, propValues, requiredTables]);
  const resolvedTableSchemas = useMemo(() => {
    if (!tableSchemas) return undefined;
    const pv = propValues as Record<string, unknown>;
    const resolved: Record<string, TableSchema> = {};
    for (const [key, schema] of Object.entries(tableSchemas)) {
      const resolvedKey = key.replace(/\{\{\$props\.(\w+)\}\}/g, (match, propKey_1) => {
        const val = pv[propKey_1];
        return val !== undefined && val !== null && val !== "" ? String(val) : match;
      });
      resolved[resolvedKey.toLowerCase()] = schema;
    }
    return resolved;
  }, [tableSchemas, propValues]);

  // Database table state — don't block on loading; treat all as missing until data arrives.
  // The listTables API can be slow for orgs that haven't used the database yet (schema provisioning).
  const {
    data: orgTables,
    isError: isTablesError,
    isLoading: isTablesLoading
  } = useOrgTables({
    enabled: resolvedTables.length > 0
  });
  const executeQuery = useExecuteQuery();
  const [isCreatingTables, setIsCreatingTables] = useState(false);
  const [reviewingTable, setReviewingTable] = useState<DBTableReference | null>(null);
  const {
    missingTables,
    missingTableNames
  } = useMemo(() => {
    if (!resolvedTables.length) return {
      missingTables: [],
      missingTableNames: new Set<string>()
    };
    let missing: typeof resolvedTables;
    if (orgTables) {
      const existingNames = new Set(orgTables.map(t => t.name.toLowerCase()));
      missing = resolvedTables.filter(t_0 => !existingNames.has(t_0.tableName.toLowerCase()));
    } else if (isTablesError) {
      missing = []; // Don't block on API failure — let user proceed
    } else {
      missing = resolvedTables; // Treat all as missing while loading
    }
    return {
      missingTables: missing,
      missingTableNames: new Set(missing.map(t_1 => t_1.tableName.toLowerCase()))
    };
  }, [resolvedTables, orgTables, isTablesError]);
  const allTablesExist = !resolvedTables.length || missingTables.length === 0;

  // Synchronous guard against double-clicks (React state update is async)
  const creatingTablesRef = useRef(new Set<string>());
  const handleCreateTable = useCallback(async (table: DBTableReference) => {
    if (creatingTablesRef.current.has(table.tableName)) throw new Error("Already creating table");
    if (!VALID_SQL_IDENTIFIER.test(table.tableName)) {
      toast.error(`Invalid table name: "${table.tableName}"`);
      throw new Error(`Invalid table name: "${table.tableName}"`);
    }
    const lowered = table.tableName.toLowerCase();
    if (RESERVED_TABLE_PREFIXES.some(p_9 => lowered.startsWith(p_9))) {
      toast.error(`Reserved table name: "${table.tableName}"`);
      throw new Error(`Reserved table name: "${table.tableName}"`);
    }
    creatingTablesRef.current.add(table.tableName);
    try {
      const explicitSchema = resolvedTableSchemas?.[table.tableName.toLowerCase()];
      let sql: string;
      if (explicitSchema) {
        sql = buildCreateTableSQL(table.tableName, explicitSchema);
      } else {
        const allCols = [...AUTO_COLUMN_DEFS, ...buildInferredColumns(table.columnHints), ...AUTO_COLUMN_DEFS_TAIL];
        const columns = allCols.map(col_0 => {
          const upperType = col_0.type.toUpperCase();
          // SERIAL/BIGSERIAL/SMALLSERIAL are implicitly NOT NULL with a sequence default —
          // skip explicit NOT NULL and any user-supplied DEFAULT (the sequence is the default).
          if (SERIAL_TYPES.has(upperType)) {
            let def_0 = `${escapeIdent(col_0.name)} ${upperType}`;
            if (col_0.primaryKey) def_0 += " PRIMARY KEY";
            if (col_0.unique) def_0 += " UNIQUE";
            return def_0;
          }
          let def_1 = `${escapeIdent(col_0.name)} ${col_0.type}`;
          if (col_0.notNull) def_1 += " NOT NULL";
          if (col_0.primaryKey) def_1 += " PRIMARY KEY";
          if (col_0.unique) def_1 += " UNIQUE";
          def_1 += formatDefaultSQL(col_0.defaultValue);
          return def_1;
        });
        sql = `CREATE TABLE ${escapeIdent(table.tableName)} (\n  ${columns.join(",\n  ")}\n)`;
      }
      await executeQuery.mutateAsync({
        sql
      });
      toast.success(`Table "${table.tableName}" created`);
    } catch (err) {
      toast.error(`Failed to create table: ${err instanceof Error ? err.message : "Unknown error"}`);
      throw err;
    } finally {
      creatingTablesRef.current.delete(table.tableName);
    }
  }, [executeQuery, resolvedTableSchemas]);
  const buildInitialColumns = useCallback((table_0: DBTableReference): ColumnDef[] => {
    const explicitSchema_0 = resolvedTableSchemas?.[table_0.tableName.toLowerCase()];
    if (explicitSchema_0) {
      return explicitSchema_0.columns.map(col_1 => ({
        name: col_1.name,
        type: col_1.type,
        primaryKey: col_1.primaryKey ?? false,
        notNull: col_1.notNull ?? false,
        unique: col_1.unique ?? false,
        defaultValue: col_1.default ?? ""
      }));
    }
    return [...AUTO_COLUMN_DEFS, ...buildInferredColumns(table_0.columnHints), ...AUTO_COLUMN_DEFS_TAIL];
  }, [resolvedTableSchemas]);
  const initialColumnsForReview = useMemo(() => reviewingTable ? buildInitialColumns(reviewingTable) : undefined, [reviewingTable, buildInitialColumns]);

  // Load connectors and wallets on mount
  useEffect(() => {
    const load = async () => {
      setIsLoadingConnectors(true);
      try {
        await Promise.all([loadConnectors(), loadWallets()]);
      } catch (error) {
        console.error("Failed to load connectors/wallets", error);
      } finally {
        setIsLoadingConnectors(false);
      }
    };
    if (open) {
      load();
    }
  }, [open, loadConnectors, loadWallets]);

  // Reset auto-select flag when modal opens/closes
  useEffect(() => {
    if (!open) {
      autoSelectDoneRef.current = false;
    }
  }, [open]);

  // Auto-select connectors once when modal first opens and data is loaded.
  // Runs only once per modal open — never re-fires when user selects/deselects.
  useEffect(() => {
    if (!open || isLoadingConnectors || autoSelectDoneRef.current) return;
    autoSelectDoneRef.current = true;
    const autoSelected: Record<string, string> = {};
    for (const required of requiredConnectors) {
      // Don't auto-select optional connectors — let the user explicitly choose
      if (required.optional) continue;
      if (isWalletType(required.type)) {
        const filtered_0 = required.accountType ? wallets.filter(w => w.accountType === required.accountType) : wallets;
        if (filtered_0.length === 1) {
          autoSelected[required.key] = filtered_0[0].id;
        }
      } else {
        const matchingConnectors = connectors.filter(c => c.type === required.type);
        if (matchingConnectors.length === 1) {
          autoSelected[required.key] = matchingConnectors[0].id;
        }
      }
    }
    if (Object.keys(autoSelected).length > 0) {
      setSelectedConnectors(prev => ({
        ...prev,
        ...autoSelected
      }));
    }
  }, [open, isLoadingConnectors, connectors, wallets, requiredConnectors]);

  // Check if connector requirements are met and required props are filled.
  // Also keep per-field invalid state so a blocked Continue click can point
  // directly at the missing inputs.
  const formValidation = useMemo(() => {
    const effectiveMin = minConnectors ?? requiredConnectors.length; // default: all required
    const invalidConnectorKeys = new Set<string>();
    const invalidPropMessages: Record<string, string> = {};

    // All mandatory (non-optional) connectors must be selected
    const mandatoryOk = requiredConnectors.filter(r => !r.optional).every(req => selectedConnectors[req.key]);

    // Total selected must meet the minimum threshold
    const selectedCount = requiredConnectors.filter(req_0 => selectedConnectors[req_0.key]).length;
    const connectorsValid = mandatoryOk && selectedCount >= effectiveMin;
    const connectorRequirementNotMet = selectedCount < effectiveMin;
    for (const required_0 of requiredConnectors) {
      if (selectedConnectors[required_0.key]) continue;
      if (!required_0.optional || connectorRequirementNotMet) {
        invalidConnectorKeys.add(required_0.key);
      }
    }
    for (const prop_3 of enrichedProps ?? []) {
      // Skip validation for conditionally hidden props
      const vw = prop_3.properties?.visibleWhen;
      if (vw && propValues[vw.propKey] !== vw.value) continue;
      const val_0 = propValues[prop_3.key];
      if (prop_3.properties?.inputType === "googleSheetUrl") {
        const trimmedValue = typeof val_0 === "string" ? val_0.trim() : "";
        if (!trimmedValue && prop_3.required) {
          invalidPropMessages[prop_3.key] = "Required";
          continue;
        }
        if (trimmedValue && sheetValidation[prop_3.key] !== true) {
          invalidPropMessages[prop_3.key] = "Check sheet access";
          continue;
        }
      }
      if (prop_3.properties?.inputType === "csvTable") {
        const columns_0 = prop_3.properties?.columns ?? [];
        // Slice the parent-owned token-info map down to just this prop's
        // columns so tokenAmount validators can enforce decimals.
        const tokenInfoByColKey = columns_0.reduce<Record<string, TokenInfo | undefined>>((acc, col_2) => {
          const info_0 = csvTableTokenInfo[`${prop_3.key}:${col_2.key}`];
          if (info_0) acc[col_2.key] = info_0;
          return acc;
        }, {});
        const csvTableValid = isCsvTableValueValid(val_0, columns_0, buildCellContext(propValues, tokenInfoByColKey), {
          required: !!prop_3.required,
          minRows: prop_3.properties?.minRows
        });
        if (!csvTableValid) {
          invalidPropMessages[prop_3.key] = isTemplateValuePresent(val_0) ? "Complete table" : "Required";
        }
        continue;
      }
      if (prop_3.required && !isTemplateValuePresent(val_0)) {
        invalidPropMessages[prop_3.key] = prop_3.key === "$notification" ? "Select channel" : "Required";
      }
    }
    const evmLogMissing = !!evmLogConfig && !selectedEventAbi;
    const propsValid = Object.keys(invalidPropMessages).length === 0;
    return {
      isFormValid: connectorsValid && propsValid && !evmLogMissing,
      invalidConnectorKeys,
      invalidPropMessages,
      evmLogMissing
    };
  }, [requiredConnectors, selectedConnectors, enrichedProps, propValues, minConnectors, csvTableTokenInfo, evmLogConfig, selectedEventAbi, sheetValidation]);
  const {
    isFormValid,
    invalidConnectorKeys: invalidConnectorKeys_0,
    invalidPropMessages: invalidPropMessages_0,
    evmLogMissing: evmLogMissing_0
  } = formValidation;

  // Check if any required (non-optional) connector types are missing from the org.
  // Optional connectors that are missing won't block the form — user can skip them.
  const missingConnectorTypes = useMemo(() => {
    const missing_0 = new Set<string>();
    for (const required_1 of requiredConnectors) {
      if (required_1.optional) continue; // Don't flag optional connectors as blocking
      if (isWalletType(required_1.type)) {
        if (wallets.length === 0) {
          missing_0.add("wallet");
        }
      } else {
        const hasConnectorOfType = connectors.some(c_0 => c_0.type === required_1.type);
        if (!hasConnectorOfType) {
          missing_0.add(required_1.type);
        }
      }
    }
    return Array.from(missing_0);
  }, [requiredConnectors, connectors, wallets]);
  const hasMissingConnectorTypes = missingConnectorTypes.length > 0;
  const hasInsufficientGroups = insufficientGroups.size > 0;

  // Handle connector selection change
  const handleSheetValidationChange = useCallback((propKey_2: string, valid: boolean) => {
    setSheetValidation(prev_0 => {
      if (prev_0[propKey_2] === valid) return prev_0;
      return {
        ...prev_0,
        [propKey_2]: valid
      };
    });
  }, []);
  const handleConnectorChange = useCallback((key_0: string, connectorId: string | null) => {
    if (connectorId === null) {
      setSelectedConnectors(prev_1 => {
        const next = {
          ...prev_1
        };
        delete next[key_0];
        return next;
      });
    } else {
      setSelectedConnectors(prev_2 => ({
        ...prev_2,
        [key_0]: connectorId
      }));
    }
  }, []);

  // When a telegram chat is selected, auto-select the corresponding bot connector
  const handleTelegramConnectorSelect = useCallback((connectorId_0: string) => {
    const telegramReq = requiredConnectors.find(r_0 => r_0.type === "telegram-bot");
    if (telegramReq) {
      setSelectedConnectors(prev_3 => ({
        ...prev_3,
        [telegramReq.key]: connectorId_0
      }));
    }
  }, [requiredConnectors]);

  // Reset telegram-chat prop values when the selected telegram-bot connector changes
  const telegramReq_0 = requiredConnectors.find(r_1 => r_1.type === "telegram-bot");
  const selectedTelegramConnectorId = telegramReq_0 ? selectedConnectors[telegramReq_0.key] : undefined;
  const prevTelegramConnectorRef = useRef(selectedTelegramConnectorId);
  useEffect(() => {
    const prev_4 = prevTelegramConnectorRef.current;
    prevTelegramConnectorRef.current = selectedTelegramConnectorId;
    // Only reset when connector actually changes (not on initial mount)
    if (prev_4 && prev_4 !== selectedTelegramConnectorId) {
      const telegramChatKeys = enrichedProps?.filter(p_10 => p_10.properties?.inputType === "telegram-chat").map(p_11 => p_11.key);
      if (telegramChatKeys?.length) {
        setPropValues(prevValues => {
          const next_0 = {
            ...prevValues
          };
          for (const k of telegramChatKeys) {
            delete next_0[k];
          }
          return next_0;
        });
      }
    }
  }, [selectedTelegramConnectorId, enrichedProps]);

  // Reset slack-channel prop values when the selected Slack connector changes
  const slackReq = requiredConnectors.find(r_2 => r_2.type === "slack");
  const selectedSlackConnectorId = slackReq ? selectedConnectors[slackReq.key] : undefined;
  const prevSlackConnectorRef = useRef(selectedSlackConnectorId);
  useEffect(() => {
    const prev_5 = prevSlackConnectorRef.current;
    prevSlackConnectorRef.current = selectedSlackConnectorId;
    if (prev_5 && prev_5 !== selectedSlackConnectorId) {
      const slackChannelKeys = enrichedProps?.filter(p_12 => p_12.properties?.inputType === "slack-channel").map(p_13 => p_13.key);
      if (slackChannelKeys?.length) {
        setPropValues(prevValues_0 => {
          const next_1 = {
            ...prevValues_0
          };
          for (const k_0 of slackChannelKeys) {
            delete next_1[k_0];
          }
          return next_1;
        });
      }
    }
  }, [selectedSlackConnectorId, enrichedProps]);
  const parsedNotificationConfig = useMemo<NotificationConfig | null>(() => {
    const raw_0 = propValues.$notification;
    if (!raw_0 || typeof raw_0 !== "string") return null;
    try {
      const parsed = JSON.parse(raw_0) as Record<string, unknown>;
      if (typeof parsed?.channel !== "string" || typeof parsed?.destination !== "string") return null;
      return parsed as unknown as NotificationConfig;
    } catch {
      return null;
    }
  }, [propValues.$notification]);
  const handleNotificationChange = useCallback((config: NotificationConfig | null) => {
    setPropValues(prev_6 => ({
      ...prev_6,
      $notification: config ? JSON.stringify(config) : undefined
    }));
  }, []);

  // Handle form submit — create missing tables first, then hand off
  const handleSubmit = useCallback(async () => {
    if (!isFormValid) return;
    if (hasMissingConnectorTypes || hasInsufficientGroups) return;
    if (csvTableTokenInfoPending) return;
    if (missingTables.length > 0) {
      setIsCreatingTables(true);
      try {
        for (const table_1 of missingTables) {
          await handleCreateTable(table_1);
        }
      } catch {
        return;
      } finally {
        setIsCreatingTables(false);
      }
    }
    const hasProps = enrichedProps?.length && Object.keys(propValues).length > 0;

    // Convert csvTable tokenAmount columns from human units (what the cell
    // stores) to atomic units (what downstream actions consume). This
    // happens once at submit time so the grid never has to juggle two
    // representations during editing.
    let convertedPropValues = propValues;
    if (hasProps) {
      const next_2 = {
        ...propValues
      };
      let changed = false;
      for (const prop_4 of enrichedProps ?? []) {
        if (prop_4.properties?.inputType !== "csvTable") continue;
        const columns_1 = prop_4.properties?.columns ?? [];
        const tokenAmountCols = columns_1.filter(col_3 => col_3.inputType === "tokenAmount").map(col_4 => ({
          key: col_4.key,
          info: csvTableTokenInfo[`${prop_4.key}:${col_4.key}`]
        }));
        if (tokenAmountCols.length === 0) continue;
        const resolved_0 = tokenAmountCols.every(c_1 => c_1.info !== undefined);
        if (!resolved_0) return;
        const val_1 = propValues[prop_4.key];
        if (!val_1 || typeof val_1 !== "object") continue;
        const converted = resolveCsvTableHumanToAtomic(val_1 as Record<string, unknown[]>, tokenAmountCols.map(c_2 => ({
          key: c_2.key,
          decimals: c_2.info!.decimals
        })));
        if (converted === null) return;
        next_2[prop_4.key] = converted as CsvTableValue;
        changed = true;
      }
      if (changed) convertedPropValues = next_2;
    }
    let payloadOverrides: Record<string, Record<string, unknown>> | undefined;
    if (evmLogConfig && selectedEventAbi) {
      payloadOverrides = {
        root: {
          eventAbi: selectedEventAbi,
          ...(selectedFilter ? {
            filter: selectedFilter
          } : {})
        }
      };
    }

    // Strip values for conditionally hidden props so stale entries don't leak to the backend.
    if (hasProps) {
      for (const prop_5 of enrichedProps ?? []) {
        const vw_0 = prop_5.properties?.visibleWhen;
        if (vw_0 && convertedPropValues[vw_0.propKey] !== vw_0.value) {
          delete convertedPropValues[prop_5.key];
        }
      }
    }
    onSubmit(selectedConnectors, hasProps ? convertedPropValues : undefined, payloadOverrides);
  }, [isFormValid, hasMissingConnectorTypes, hasInsufficientGroups, csvTableTokenInfoPending, missingTables, handleCreateTable, selectedConnectors, propValues, enrichedProps, csvTableTokenInfo, onSubmit, evmLogConfig, selectedEventAbi, selectedFilter]);
  const isContinueBusy = isSubmitting || isCreatingTables;
  const isContinueBlocked = !isFormValid || hasMissingConnectorTypes || hasInsufficientGroups || csvTableTokenInfoPending;
  const canContinue = !isContinueBusy && !isContinueBlocked;
  const revealMissingInputs = useCallback(() => {
    setShowMissingHints(true);
    window.requestAnimationFrame(() => {
      const firstMissingField = modalContentRef.current?.querySelector<HTMLElement>('[data-template-missing="true"]');
      firstMissingField?.scrollIntoView({
        block: "center",
        behavior: "smooth"
      });
    });
  }, []);
  const handleContinueClick = useCallback(() => {
    if (isContinueBusy || csvTableTokenInfoPending) return;
    if (!canContinue) {
      revealMissingInputs();
      return;
    }
    void handleSubmit();
  }, [canContinue, csvTableTokenInfoPending, handleSubmit, isContinueBusy, revealMissingInputs]);

  // Handle modal close
  const handleClose = useCallback((open_0: boolean) => {
    if (!open_0) {
      // Reset form when closing
      setSelectedConnectors({});
      setWorkflowWalletAddress(undefined);
      setInsufficientGroups(new Set());
      setSheetValidation({});
      setShowMissingHints(false);
      const defaults: Record<string, PropValue> = {};
      (enrichedProps ?? templateProps)?.forEach(prop_6 => {
        if (prop_6.default !== undefined) {
          defaults[prop_6.key] = coercePropDefault(prop_6);
        } else if (prop_6.type === "array") {
          defaults[prop_6.key] = [];
        } else {
          defaults[prop_6.key] = undefined;
        }
      });
      setPropValues(defaults);
      setSelectedEventAbi(undefined);
      setSelectedFilter(undefined);
    }
    onOpenChange(open_0);
  }, [onOpenChange, enrichedProps, templateProps]);

  // If no required connectors, no template props, and no required tables, don't show modal
  if (requiredConnectors.length === 0 && (!templateProps || templateProps.length === 0) && !resolvedTables.length) {
    return null;
  }
  return <ResponsiveModal open={open} onOpenChange={handleClose} title="" contentClass="max-w-2xl">
      <div ref={modalContentRef} className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <GlassIcon color="blue" size="lg" icon={IconPlugOutline18} />
          </div>
          <div className="flex-1">
            <h2 className="heading-lg">{title}</h2>
            <p className="body-sm mt-1">{description}</p>
          </div>
        </div>

        {/* Missing connector types warning — only show after loading to avoid false positives */}
        {!isLoadingConnectors && missingConnectorTypes.length > 0 && <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <IconAlertWarningOutline18 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="flex-1">
              <p className="text-xs font-medium">
                Missing {missingConnectorTypes.includes("wallet") ? "Wallets or Connectors" : "Connectors"}
              </p>
              <p className="mt-1 text-xs">
                You need to create the following before you can use this template:{" "}
                <span className="font-semibold">
                  {missingConnectorTypes.map(type_0 => type_0 === "wallet" ? "Workflow Wallet" : type_0).join(", ")}
                </span>
              </p>
              {missingConnectorTypes.includes("wallet") ? <Link href="/wallet-management" target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs underline">
                  <IconPlusOutline18 className="h-3 w-3" />
                  Go to Wallet Management
                </Link> : <Link href="/connectors" target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs underline">
                  <IconPlusOutline18 className="h-3 w-3" />
                  Go to Connectors page
                </Link>}
            </div>
          </div>}

        {/* Connector Selection Form */}
        {isLoadingConnectors ? <div className="animate-in fade-in space-y-4 duration-300">
            {Array.from({
          length: 3
        }).map((_, i) => <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>)}
          </div> : <div className="space-y-4">
            {requiredConnectors.map(required_2 => {
          // Use WalletSelector for Turnkey wallets, ConnectorSelector for everything else
          if (isWalletType(required_2.type)) {
            return <MissingInputFrame key={required_2.key} active={showMissingHints && invalidConnectorKeys_0.has(required_2.key)} message="Select wallet">
                    <WalletSelector walletId={selectedConnectors[required_2.key]} onChange={(walletId, walletAddress) => {
                handleConnectorChange(required_2.key, walletId);
                setWorkflowWalletAddress(walletAddress);
              }} label={required_2.label || "Workflow Wallet"} description={required_2.description || "Select a wallet for blockchain transactions"} required autoSelectDefault={false} accountType={required_2.accountType} />
                  </MissingInputFrame>;
          }
          return <MissingInputFrame key={required_2.key} active={showMissingHints && invalidConnectorKeys_0.has(required_2.key)} message="Select connector">
                  <ConnectorSelector connectorType={required_2.type} value={selectedConnectors[required_2.key]} onChange={connectorId_1 => handleConnectorChange(required_2.key, connectorId_1)} label={required_2.label} description={required_2.description} required={!required_2.optional} />
                </MissingInputFrame>;
        })}
          </div>}

        {notificationProp && <div className="space-y-4">
            {requiredConnectors.length > 0 && <div className="border-ds-border-default border-t pt-4">
                <h3 className="heading-lg">Notification Channel</h3>
              </div>}
            {!requiredConnectors.length && <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <IconSliders2Outline18 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="body-sm-medium">Notification Channel</h3>
                  <p className="label-xs">Choose where to receive notifications</p>
                </div>
              </div>}
            <MissingInputFrame active={showMissingHints && !!invalidPropMessages_0.$notification} message={invalidPropMessages_0.$notification ?? "Select channel"}>
              <NotificationChannelPicker value={parsedNotificationConfig} onChange={handleNotificationChange} allowedChannels={notificationAllowedChannels} />
            </MissingInputFrame>
          </div>}

        {/* Template Props */}
        {regularProps && regularProps.length > 0 && <div className="space-y-6">
            {(requiredConnectors.length > 0 || notificationProp) && <div className="border-ds-border-default border-t pt-4">
                <h3 className="heading-lg">Configure Parameters</h3>
              </div>}
            {!requiredConnectors.length && !notificationProp && <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <IconSliders2Outline18 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="body-sm-medium">Configure Parameters</h3>
                  <p className="label-xs">Fill in the values for this template</p>
                </div>
              </div>}
            {(() => {
          const renderedGroupKeys = new Set<string>();
          return regularProps.map(prop_7 => {
            // Conditional visibility: skip props whose visibleWhen condition isn't met
            const vw_1 = prop_7.properties?.visibleWhen;
            if (vw_1 && propValues[vw_1.propKey] !== vw_1.value) return null;
            const gk_1 = prop_7.properties?.groupKey;

            // If this prop belongs to a recognized group, render the group component once
            if (gk_1 && groupComponents.has(gk_1)) {
              if (renderedGroupKeys.has(gk_1)) return null;
              renderedGroupKeys.add(gk_1);
              const gProps_0 = propGroups.get(gk_1)!;
              const chainProp = gProps_0.find(p_14 => p_14.properties?.inputType === "chainSelector");
              const tokenProp = gProps_0.find(p_15 => p_15.properties?.inputType === "tokenSelector");
              const amountProp = gProps_0.find(p_16 => p_16.properties?.inputType === "tokenAmount");
              const componentType = groupComponents.get(gk_1)!;
              const groupInvalidMessage = gProps_0.map(groupProp => invalidPropMessages_0[groupProp.key]).find(Boolean) ?? (insufficientGroups.has(gk_1) ? "Insufficient balance" : undefined);
              if (componentType === "tokenChainAmount" && chainProp && tokenProp && amountProp) {
                const groupLabel = gk_1.charAt(0).toUpperCase() + gk_1.slice(1);
                return <MissingInputFrame key={`group-${gk_1}`} active={showMissingHints && !!groupInvalidMessage} message={groupInvalidMessage ?? "Required"}>
                        <div className="space-y-2">
                          <label className="label label-xs text-ds-text-primary dark:text-zinc-100">
                            {groupLabel}
                            {amountProp.required && <span className="ml-0.5 text-red-500">*</span>}
                          </label>
                          <TokenChainAmountInput amount={propValues[amountProp.key] != null ? String(propValues[amountProp.key]) : ""} onAmountChange={v_0 => {
                      setPropValues(prev_7 => ({
                        ...prev_7,
                        [amountProp.key]: v_0 || undefined
                      }));
                    }} tokenAddress={propValues[tokenProp.key] != null ? String(propValues[tokenProp.key]) : ""} chainId={propValues[chainProp.key] != null ? Number(propValues[chainProp.key]) : 0} onTokenChange={(addr, chain, symbol, decimals) => {
                      setPropValues(prev_8 => {
                        const next_3 = {
                          ...prev_8,
                          [tokenProp.key]: addr,
                          [chainProp.key]: chain,
                          [`${gk_1}_token_symbol`]: symbol,
                          [`${gk_1}_token_decimals`]: decimals
                        };
                        // Reset amount when token changes
                        delete next_3[amountProp.key];
                        return next_3;
                      });
                    }} workflowWalletAddress={workflowWalletAddress} excludeNative={tokenProp.properties?.excludeNative} onInsufficientBalance={insufficient => setInsufficientGroups(prev_9 => {
                      if (prev_9.has(gk_1) === insufficient) return prev_9;
                      const next_4 = new Set(prev_9);
                      if (insufficient) next_4.add(gk_1);else next_4.delete(gk_1);
                      return next_4;
                    })} popoverModal />
                        </div>
                      </MissingInputFrame>;
              }
              if (componentType === "tokenChain" && chainProp && tokenProp) {
                const groupLabel_0 = gk_1.charAt(0).toUpperCase() + gk_1.slice(1);
                return <MissingInputFrame key={`group-${gk_1}`} active={showMissingHints && !!groupInvalidMessage} message={groupInvalidMessage ?? "Required"}>
                        <div className="space-y-2">
                          <label className="label label-xs text-ds-text-primary dark:text-zinc-100">
                            {groupLabel_0}
                            {tokenProp.required && <span className="ml-0.5 text-red-500">*</span>}
                          </label>
                          <TokenChainInput tokenAddress={propValues[tokenProp.key] != null ? String(propValues[tokenProp.key]) : ""} chainId={propValues[chainProp.key] != null ? Number(propValues[chainProp.key]) : 0} onTokenChange={(addr_0, chain_0, symbol_0, decimals_0) => {
                      setPropValues(prev_10 => ({
                        ...prev_10,
                        [tokenProp.key]: addr_0,
                        [chainProp.key]: chain_0,
                        [`${gk_1}_token_symbol`]: symbol_0,
                        [`${gk_1}_token_decimals`]: decimals_0
                      }));
                    }} workflowWalletAddress={workflowWalletAddress} excludeNative={tokenProp.properties?.excludeNative} popoverModal />
                        </div>
                      </MissingInputFrame>;
              }
            }

            // Default: render individual prop (existing code)
            const sources_0 = prop_7.properties?.sources ?? [];
            const nodeLabels = sources_0.map(s_2 => s_2.nodeLabel);
            const uniqueLabels = [...new Set(nodeLabels)];
            const nodeLabel = sources_0.length > 0 ? uniqueLabels.length === 1 && sources_0.length > 1 ? `${uniqueLabels[0]} ×${sources_0.length}` : uniqueLabels.join(", ") : undefined;
            const isGoogleSheetProp = googleSheetTemplateProp?.default && sources_0.some(s_3 => {
              const nodeType_0 = nodeTypesByNodeId[(s_3 as {
                nodeId: string;
              }).nodeId];
              return nodeType_0?.startsWith(GOOGLE_SHEETS_NODE_PREFIX);
            });
            const invalidMessage = invalidPropMessages_0[prop_7.key];
            return <MissingInputFrame key={prop_7.key} active={showMissingHints && !!invalidMessage} message={invalidMessage ?? "Required"}>
                    <div className="space-y-2">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <label className="label label-xs text-ds-text-primary dark:text-zinc-100">
                            {prop_7.name || prop_7.key}
                            {prop_7.required && <span className="ml-0.5 text-red-500">*</span>}
                          </label>
                        </div>
                        {isGoogleSheetProp ? <div className="mt-2 flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-950/20">
                            <div className="flex-1 space-y-1">
                              <p className="text-sm text-green-700 dark:text-green-300">
                                Click below to copy the template to your Google Drive, then paste your new sheet URL
                                here.
                              </p>
                              {/^https:\/\//i.test(String(googleSheetTemplateProp.default ?? "")) ? <a href={String(googleSheetTemplateProp.default ?? "")} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-sm font-medium text-green-700 underline hover:text-green-900 dark:text-green-300 dark:hover:text-green-100">
                                  Copy Google Sheet Template &rarr;
                                </a> : <span className="mt-1 inline-block text-sm text-green-700 dark:text-green-300">
                                  {String(googleSheetTemplateProp.default ?? "")}
                                </span>}
                            </div>
                          </div> : (prop_7.description || nodeLabel) && <div className="mt-0.5 flex items-center gap-1.5">
                              {prop_7.description && <p className="label-xs">{prop_7.description}</p>}
                            </div>}
                      </div>
                      <TemplatePropValueInput prop={prop_7} value={propValues[prop_7.key]} onChange={newVal => {
                  setPropValues(prev_11 => {
                    const next_5 = {
                      ...prev_11,
                      [prop_7.key]: newVal
                    };
                    if (prop_7.properties?.inputType === "chainSelector" && enrichedProps) {
                      for (const dep of enrichedProps) {
                        if (dep.properties?.chainPropKey === prop_7.key) {
                          delete next_5[dep.key];
                        }
                      }
                    }
                    return next_5;
                  });
                }} onPropChange={(propKey_3, newVal_0) => {
                  setPropValues(prev_12 => ({
                    ...prev_12,
                    [propKey_3]: newVal_0
                  }));
                }} propValues={propValues} allProps={enrichedProps} connectorMapping={selectedConnectors} onTelegramConnectorSelect={handleTelegramConnectorSelect} workflowWalletAddress={workflowWalletAddress} nodeTypesByNodeId={nodeTypesByNodeId} actionByType={actionByType} csvTableTokenInfo={csvTableTokenInfo} onValidationStateChange={handleSheetValidationChange} />
                    </div>
                  </MissingInputFrame>;
          });
        })()}
          </div>}

        {/* Event ABI Selection (evm-log templates) */}
        {evmLogConfig && <div className="space-y-4">
            <div className="border-ds-border-default border-t pt-4">
              <h3 className="heading-lg">Event Configuration</h3>
              <p className="label-xs mt-1">Select which smart contract event to monitor</p>
            </div>
            {evmLogConfig.contractPropKey && !propValues[evmLogConfig.contractPropKey] && <p className="label-xs text-ds-text-subtle">Please select a contract address first</p>}
            <MissingInputFrame active={showMissingHints && evmLogMissing_0} message="Select event">
              <EventAbiSelector label="Event ABI" value={selectedEventAbi ?? ""} onChange={(val_2: string) => {
            setSelectedEventAbi(val_2);
            setSelectedFilter(undefined);
          }} chainId={evmLogConfig.chainPropKey ? propValues[evmLogConfig.chainPropKey] : undefined} contractAddress={evmLogConfig.contractPropKey ? propValues[evmLogConfig.contractPropKey] : undefined} />
            </MissingInputFrame>
            {selectedEventAbi && <FilterBuilder label="Event Filter (Optional)" description="Add conditions to filter which events trigger this workflow" value={selectedFilter ?? ""} onChange={setSelectedFilter} eventAbi={selectedEventAbi} />}
          </div>}

        {/* Database Tables Section */}
        {resolvedTables.length > 0 && <div className="space-y-4">
            <div className="border-t border-zinc-200 pt-4 dark:border-zinc-700" />
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <IconDatabaseOutline18 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="body-sm-medium">Database Tables</h3>
                <p className="label-xs">This template uses database queries that require the following tables</p>
              </div>
            </div>

            <div className="space-y-2">
              {resolvedTables.map(table_2 => {
            const exists = !isTablesError && !missingTableNames.has(table_2.tableName.toLowerCase());
            const unknown = isTablesError;
            // Prefer the declared table_schemas column count over the SQL-derived hints,
            // since the template's declared schema is the source of truth for what will be created.
            const declaredColumnCount = resolvedTableSchemas?.[table_2.tableName.toLowerCase()]?.columns.length;
            const detectedColumnCount = declaredColumnCount ?? table_2.columnHints.length;
            return <div key={table_2.tableName} className="flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2.5 dark:border-zinc-700 dark:bg-zinc-800/50">
                    <div className="flex items-center gap-2">
                      {unknown ? <IconDatabaseOutline18 className="h-4 w-4 text-zinc-400" /> : exists ? <IconCircleCheckOutline18 className="h-4 w-4 text-green-500" /> : <IconAlertWarningOutline18 className="h-4 w-4 text-amber-500" />}
                      <span className="font-mono text-sm text-zinc-900 dark:text-zinc-100">{table_2.tableName}</span>
                      {detectedColumnCount > 0 && !exists && !unknown && <span className="text-xs text-zinc-500 dark:text-zinc-400">
                          ({detectedColumnCount} column{detectedColumnCount !== 1 ? "s" : ""} detected)
                        </span>}
                    </div>
                    {unknown ? <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Unknown</span> : exists ? <span className="text-xs font-medium text-green-600 dark:text-green-400">Ready</span> : <div className="flex items-center gap-1.5">
                        <Button type="button" variant="white" onClick={() => setReviewingTable(table_2)} disabled={isTablesLoading} className="h-7 px-2">
                          <span className="flex items-center gap-1 text-xs">
                            <IconEyeOutline18 className="h-3 w-3" />
                            Review
                          </span>
                        </Button>
                        <span className="text-xs font-medium text-amber-600 dark:text-amber-400">New</span>
                      </div>}
                  </div>;
          })}
            </div>

            {isTablesError && <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300">
                <IconAlertWarningOutline18 className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400" />
                <p className="text-xs">
                  Unable to check table status. You can still proceed &mdash; verify tables exist on the Databases page.
                </p>
              </div>}

            {!allTablesExist && !isTablesError && <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Missing tables will be created automatically when you continue.
              </p>}

            {reviewingTable && <CreateTableModal key={reviewingTable.tableName} open onOpenChange={v_1 => !v_1 && setReviewingTable(null)} initialTableName={reviewingTable.tableName} initialColumns={initialColumnsForReview} onCreated={() => setReviewingTable(null)} />}
          </div>}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={() => handleClose(false)} disabled={isSubmitting} className="border-ds-neutral-300 text-ds-text-secondary dark:border-ds-neutral-700 dark:hover:bg-ds-neutral-700 bg-ds-surface-card hover:bg-ds-surface-hover flex-1 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-50">
            Cancel
          </button>
          <Button onClick={handleContinueClick} disabled={isContinueBusy || csvTableTokenInfoPending} aria-disabled={!canContinue} className={cn("flex-1", !canContinue && !isContinueBusy && "opacity-50")}>
            <IconPlugOutline18 className="mr-2 h-4 w-4" />
            {isCreatingTables ? "Setting up tables..." : isSubmitting ? "Creating..." : csvTableTokenInfoPending ? "Loading token info…" : "Continue"}
          </Button>
        </div>
      </div>
    </ResponsiveModal>;
}